import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { QuickInfoBar } from '../components/QuickInfoBar';
import { FrankiesVideoSection } from '../components/FrankiesVideoSection';
import { WhatPeopleSay } from '../components/WhatPeopleSay';
import {
  Utensils,
  Calendar,
  UtensilsCrossed,
  ArrowRight,
  Flame,
  Clock,
  MapPin,
  Sparkles,
  MessageSquare,
  Check,
} from 'lucide-react';
import { ASSETS, OFFICIAL_MENU, CLIENT_IMAGES } from '../data/restaurantData';
import { ClientImage } from '../components/ClientImage';
import { useImages } from '../context/ImageContext';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { clientImages } = useImages();

  const handleWhatsAppContact = () => {
    const phoneNumber = '447554663569';
    const message = encodeURIComponent(
      "Hello Frankie's @ The Beach! 🌊🍔 I'd like to ask about your opening times and seaside menu today."
    );
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex-1 bg-[#EEEFE9]">
      {/* 1. Hero Section */}
      <Hero
        onExploreMenu={() => navigate('/menu')}
        onContact={() => navigate('/contact')}
      />

      {/* 2. Feature Highlights Quick Bar (Rides, Quick Bites, Bar, Loaded Fries) */}
      <QuickInfoBar />

      {/* 3. Frankie's Beach Moments Video Section (Video 1, Video 2, Video 3) */}
      <FrankiesVideoSection />

      {/* 4. Feature Showcase / Food Kiosk & Bar Teaser */}
      <section className="py-16 sm:py-20 bg-white border-t border-b border-[#dde0d5] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Image Collage */}
            <div className="lg:col-span-6 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[radial-gradient(ellipse_at_50%_35%,#0084FF_0%,#0070E0_50%,#003E99_100%)]">
                <ClientImage
                  src={clientImages.kiosk}
                  slotKey="site:kiosk"
                  fallbackSrc={ASSETS.beachPatio}
                  alt="Frankie's Beachfront Food Kiosk and Bar in Ramsgate"
                  className="w-full h-80 sm:h-96 object-cover"
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <span className="bg-gradient-to-r from-[#ECD87A] via-[#D1A03F] to-[#8C6F2B] text-[#000000] text-[11px] font-black uppercase px-3 py-1 rounded-md tracking-wider">
                    UK'S #1 QUICK BITE (TRIPADVISOR)
                  </span>
                  <p className="font-heading font-extrabold text-xl sm:text-2xl mt-2 text-white">
                    Right on the sands of Ramsgate Beach
                  </p>
                </div>
              </div>

              {/* Floating Polaroids / Pill */}
              <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-white p-3.5 rounded-2xl shadow-xl border border-[#dde0d5] flex items-center gap-3 max-w-xs">
                <ClientImage
                  src={clientImages.food1Burger}
                  slotKey="menu:beef-burger"
                  fallbackSrc={ASSETS.fallbacks.heroBurger}
                  alt="6oz Steak Burger"
                  className="w-14 h-14 rounded-xl object-cover"
                  priority
                />
                <div>
                  <div className="flex items-center gap-1 text-[#D1A03F]">
                    <Sparkles className="w-3.5 h-3.5 fill-current" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#0070E0]">
                      100% Prime Beef
                    </span>
                  </div>
                  <h4 className="font-heading font-extrabold text-xs text-[#000000] uppercase">
                    6oz Prime Steak
                  </h4>
                  <p className="text-[11px] text-gray-500">Cooked to juicy perfection</p>
                </div>
              </div>
            </div>

            {/* Right Story & Highlights */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#0070E0] font-heading">
                <span className="text-base">🌊</span>
                <span>FUN, FOOD, DRINKS &amp; SEASIDE VIBES</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-[#000000] font-heading leading-tight">
                RIGHT ON THE MAIN SANDS OF OUR AWARD-WINNING BEACH.
              </h2>

              <p className="text-sm sm:text-base text-[#496068] leading-relaxed">
                <strong className="text-[#000000]">Frankies At The Beach</strong> is the ultimate seaside destination for fun, food, and good times! From thrilling fair rides for the children to our award-winning quick bites, a well-stocked bar for the adults, loaded fries, fresh warm doughnuts, and great music — everything you love about the seaside is right here in one fantastic location.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/menu"
                  className="bg-[#0070E0] hover:bg-[#005FCE] text-white font-heading font-extrabold text-xs uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-[0_8px_20px_rgba(0,112,224,0.35)] transition-all flex items-center gap-2 cursor-pointer group"
                >
                  <span>COME AND ENJOY FRANKIES</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>

                <Link
                  to="/about"
                  className="bg-[#EEEFE9] hover:bg-[#e6eadf] text-[#000000] font-heading font-extrabold text-xs uppercase tracking-wider px-5 py-3.5 rounded-xl border border-[#dde0d5] transition-all cursor-pointer"
                >
                  READ OUR STORY
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* 5. Menu Categories Teaser (Direct access to all categories) */}
      <section className="py-16 sm:py-20 bg-[#edf0e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-extrabold uppercase tracking-widest text-[#0070E0] font-heading">
              WHAT ARE YOU CRAVING TODAY?
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#000000] font-heading mt-1">
              EXPLORE OUR SEASIDE SPECIALS
            </h2>
            <div className="flex items-center justify-center gap-2 my-3 text-[#D1A03F]">
              <div className="h-0.5 w-6 bg-[#D1A03F]/50 rounded-full" />
              <UtensilsCrossed className="w-4 h-4 text-[#D1A03F]" />
              <div className="h-0.5 w-6 bg-[#D1A03F]/50 rounded-full" />
            </div>
            <p className="text-sm text-[#496068]">
              From footlong hot dogs to loaded seaside fries, discover what makes Frankie's Ramsgate's favourite food kiosk and bar.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 max-w-7xl mx-auto gap-5">
            {/* Category Card 1: Burgers */}
            <div
              onClick={() => navigate('/menu?category=burgers')}
              className="bg-white rounded-2xl overflow-hidden border border-[#dde0d5] shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <ClientImage
                  src={clientImages.caribbeanChilliBurger || '/dishes/Caribbean Chilli Cheese Burger.webp'}
                  slotKey="menu:caribbean-chilli-cheese-burger"
                  fallbackSrc="/dishes/Caribbean Chilli Cheese Burger.png"
                  alt="Caribbean Chilli Cheese Burger - 6oz Burgers"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading font-extrabold text-base text-[#000000] uppercase group-hover:text-[#0070E0] transition-colors">
                    6oz Burgers
                  </h3>
                  <p className="text-xs text-[#526b74] mt-1 line-clamp-2">
                    Juicy 6oz steak patties cooked to perfection with mature cheddar, thick Danish bacon, stilton, or specialty bajan sauce.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-[#edf0e6] flex items-center justify-between text-xs font-bold text-[#0070E0]">
                  <span>View Burgers</span>
                  <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    Explore →
                  </span>
                </div>
              </div>
            </div>

            {/* Category Card 2: Fries & Loaded Fries */}
            <div
              onClick={() => navigate('/menu?category=loaded-fries')}
              className="bg-white rounded-2xl overflow-hidden border border-[#dde0d5] shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <ClientImage
                  src={clientImages.pepperoniFries}
                  slotKey="menu:pepperoni-cheese-loaded-fries"
                  fallbackSrc={ASSETS.loadedFries}
                  alt="Fries & Loaded Fries"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading font-extrabold text-base text-[#000000] uppercase group-hover:text-[#0070E0] transition-colors">
                    Fries &amp; Loaded Fries
                  </h3>
                  <p className="text-xs text-[#526b74] mt-1 line-clamp-2">
                    Crispy skin-on fries and loaded fries piled high with torched mature cheddar, pepperoni, bacon, fish fingers, or southern fried chicken.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-[#edf0e6] flex items-center justify-between text-xs font-bold text-[#0070E0]">
                  <span>View Fries</span>
                  <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    Explore →
                  </span>
                </div>
              </div>
            </div>

            {/* Category Card 3: Hot Fresh Donuts - Frankie's Signature Blue Theme */}
            <div
              onClick={() => navigate('/menu?category=donuts')}
              className="bg-white rounded-2xl overflow-hidden border-2 border-[#0070E0]/30 shadow-sm hover:shadow-xl hover:border-[#0070E0] transition-all cursor-pointer group flex flex-col justify-between relative"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-sky-50">
                <ClientImage
                  src="/donuts/donuts-biscoff.webp"
                  slotKey="menu:hot-donuts"
                  fallbackSrc="/donuts/donuts-biscoff.jpg"
                  alt="Fresh Hot Donuts"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-2.5 left-2.5 flex flex-col gap-1 items-start">
                  <span className="bg-[#0070E0] text-white text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full shadow-sm tracking-wider flex items-center gap-1">
                    <span>HOT DONUTS</span>
                    <span>🍩</span>
                  </span>
                  <span className="bg-amber-400 text-black text-[9px] font-black uppercase px-2 py-0.5 rounded-full shadow-xs tracking-wider">
                    20 FLAVOURS
                  </span>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between bg-gradient-to-b from-white to-[#f0f7ff]/40">
                <div>
                  <h3 className="font-heading font-extrabold text-base text-[#000000] uppercase group-hover:text-[#0070E0] transition-colors">
                    Hot Donuts
                  </h3>
                  <p className="text-xs text-[#526b74] mt-1 line-clamp-2">
                    Fresh warm sugared ring donuts, soft-serve ice cream combos, and 20 topped flavours including Biscoff, Oreo &amp; Kinder Bueno.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-[#edf0e6] flex items-center justify-between text-xs font-bold text-[#0070E0]">
                  <span>Explore Donut Menu</span>
                  <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    View All 20 Flavours →
                  </span>
                </div>
              </div>
            </div>

            {/* Category Card 4: Refreshing Slushies & Drinks */}
            <div
              onClick={() => navigate('/menu?category=drinks')}
              className="bg-white rounded-2xl overflow-hidden border border-[#dde0d5] shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <ClientImage
                  src="/dishes/Strawberry Slushy 568ml.webp"
                  slotKey="menu:strawberry-slushy-568ml"
                  fallbackSrc="/Strawberry Slushy 568ml.webp"
                  alt="Refreshing Slushies & Drinks"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading font-extrabold text-base text-[#000000] uppercase group-hover:text-[#0070E0] transition-colors">
                    Drinks &amp; Slushies
                  </h3>
                  <p className="text-xs text-[#526b74] mt-1 line-clamp-2">
                    Refreshing 568ml pint slushies in strawberry, blue raspberry, sour cherry, Coca-Cola, lemon &amp; lime, and apple &amp; mango.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-[#edf0e6] flex items-center justify-between text-xs font-bold text-[#0070E0]">
                  <span>View Drinks</span>
                  <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    Explore →
                  </span>
                </div>
              </div>
            </div>

            {/* Category Card 4: Beach Bar & Alcohol */}
            <div
              onClick={() => navigate('/menu?category=alcohol')}
              className="bg-white rounded-2xl overflow-hidden border border-amber-300/80 shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-amber-50">
                <ClientImage
                  src="/drinks/cruzcampo-pint.webp"
                  slotKey="menu:cruzcampo-draught-pint"
                  fallbackSrc="/drinks/cruzcampo-pint.jpg"
                  alt="Beach Bar & Alcohol"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 bg-[#D1A03F] text-black text-[10px] font-extrabold uppercase px-2 py-0.5 rounded shadow-sm">
                  Beach Bar 🍺
                </span>
              </div>
              <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading font-extrabold text-base text-[#000000] uppercase group-hover:text-[#0070E0] transition-colors">
                    Beach Bar &amp; Alcohol
                  </h3>
                  <p className="text-xs text-[#526b74] mt-1 line-clamp-2">
                    Draught Cruzcampo &amp; Fosters, Strongbow cider, Moretti &amp; Peroni, spirits, cocktails, and wine or Prosecco.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-[#edf0e6] flex items-center justify-between text-xs font-bold text-[#0070E0]">
                  <span>View Beach Bar</span>
                  <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    Explore →
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-gradient-to-b from-[#ECD87A] via-[#D1A03F] to-[#8C6F2B] hover:brightness-105 active:scale-95 text-[#000000] font-heading font-extrabold text-xs sm:text-sm uppercase tracking-wider px-7 py-3.5 rounded-xl shadow-md transition-all cursor-pointer"
            >
              <span>VIEW FULL SEASIDE MENU</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. What People Say (Customer reviews showcase) */}
      <WhatPeopleSay
        onViewAllReviews={() => navigate('/reviews')}
        onOpenGallery={() => navigate('/gallery')}
      />

      {/* 7. Call To Action: Visit & Contact Banner */}
      <section className="bg-[radial-gradient(ellipse_at_50%_35%,#0084FF_0%,#0070E0_50%,#0048B8_100%)] py-16 sm:py-20 text-white text-center relative overflow-hidden shadow-2xl border-t-2 border-white/20">
        {/* Luminous seaside blue ambiance */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(255,255,255,0.16)_0%,transparent_65%)] pointer-events-none" />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#0077FF]/35 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#003899]/50 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 space-y-5">
          <div className="inline-flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-widest font-heading">
            <UtensilsCrossed className="w-4 h-4 text-amber-300" />
            <span>JOIN US BY THE SEA</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase font-heading">
            PLANNING YOUR VISIT TO RAMSGATE MAIN SANDS?
          </h2>

          <p className="text-sm sm:text-base text-sky-100 max-w-2xl mx-auto leading-relaxed">
            Drop by our food kiosk and bar at Kiosk 1, Marina Esplanade for 6oz steak burgers, loaded chips, cold drinks, footlong dogs, and kids' beach rides right on the sand. Walk-ins are always welcomed by the beach!
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-3">
            <Link
              to="/contact"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/30 text-xs sm:text-sm font-semibold px-6 py-3.5 rounded-xl transition-all cursor-pointer min-h-[44px] flex items-center justify-center"
              title="Get Directions & Contact Details"
              aria-label="Get Directions and Contact Details for Frankie's"
            >
              Directions &amp; Contact
            </Link>

            <button
              onClick={handleWhatsAppContact}
              className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-heading font-extrabold text-xs sm:text-sm uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-lg flex items-center gap-2 cursor-pointer transition-all min-h-[44px]"
              title="Chat with Frankie's team on WhatsApp"
              aria-label="Chat with Frankie's team on WhatsApp"
            >
              <MessageSquare className="w-4 h-4 fill-current" />
              <span>Chat on WhatsApp</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
